/**
 * onePagerExport.ts — pure assembler that turns "data we have" into the render-ready one-pager
 * bundle (T-258).
 *
 * Given a tract, the searched address, the state label, a data vintage and (T-451) the nearby
 * competition/amenity stats, it returns the { model, filename } bundle. Pure and deterministic
 * (no map, no Date, no DOM) so it is unit-testable; the actual PDF download lives in a thin,
 * separately-verified layer.
 *
 * T-451 (revised, 2026-07-23): the schematic site-locator was removed from the one-pager, so this
 * assembler no longer captures live-map geometry or projects a locator. See OnePagerDocument for
 * the why; the prior projection code is in git history at ae08da4^.
 */
import { buildOnePagerModel, type OnePagerModel } from './onePagerModel'
import {
  buildOnePagerModelV2,
  type OnePagerV2Model,
  type BlockInputs,
  type BlockGroupInputs,
  type StatePercentiles,
} from './onePagerModelV2'
import type { NearbyStats } from '../nearbyStats'
import type { CensusTract, SiteScoreResult } from '../tractModel'
import type { LocationClassification } from '../locationClassification'

export interface ExportBundle {
  model: OnePagerModel
  filename: string
}

export interface ExportBundleV2 {
  model: OnePagerV2Model
  filename: string
}

/**
 * Stable, filesystem-safe export name, e.g. "OptiYield_13121002700_GA.pdf".
 *
 * Deliberately the ABBREVIATION even though the document header spells the state out (T-387):
 * exported files sort and scan by code, and every file already downloaded uses this form.
 */
export function exportFilename(tract: CensusTract, stateLabel: string): string {
  const safeState = stateLabel.replace(/[^A-Za-z0-9]/g, '') || 'US'
  return `OptiYield_${tract.tractId}_${safeState}.pdf`
}

export function buildExportForTract(args: {
  tract: CensusTract
  address?: string | null
  /** T-478 — true when `address` was reverse-geocoded from a map click (approximate), not typed. */
  addressApproximate?: boolean
  stateLabel: string
  dataVintage: string
  year: number
  /** T-451 nearby competition/amenities, already gathered. Null when there is no searched point
   *  or a lookup failed — the document prints a one-line explanation in its place. */
  nearby?: NearbyStats | null
  /** T-527 — the site score already loaded for the panel. Threaded into the model so the PDF
   *  headlines the same site Opportunity Score the "View all metrics" panel shows, with the tract
   *  LVI as a labeled baseline. Null in the tract-only (no site scored) state. */
  siteScore?: SiteScoreResult | null
  /** T-498 — the site's block/block-group classification (fetched for the export). Threaded into
   *  the model so the PDF features the same corridor/retail/residential class the panel shows. Null
   *  when there was no pin or the vicinity lookup failed — the block then prints an honest note. */
  classification?: LocationClassification | null
}): ExportBundle {
  const {
    tract,
    address = null,
    addressApproximate = false,
    stateLabel,
    dataVintage,
    year,
    nearby = null,
    siteScore = null,
    classification = null,
  } = args
  const model = buildOnePagerModel({
    tract,
    address,
    addressApproximate,
    stateLabel,
    dataVintage,
    year,
    nearby,
    siteScore,
    classification,
  })
  return { model, filename: exportFilename(tract, stateLabel) }
}

export function buildExportForTractV2(args: {
  tract: CensusTract
  address?: string | null
  addressApproximate?: boolean
  stateLabel: string
  dataVintage: string
  year: number
  nearby?: NearbyStats | null
  siteScore?: SiteScoreResult | null
  classification?: LocationClassification | null
  blockInputs?: BlockInputs | null
  blockGroupInputs?: BlockGroupInputs | null
  statePercentiles?: StatePercentiles | null
}): ExportBundleV2 {
  const {
    tract,
    address = null,
    addressApproximate = false,
    stateLabel,
    dataVintage,
    year,
    nearby = null,
    siteScore = null,
    classification = null,
    blockInputs = null,
    blockGroupInputs = null,
    statePercentiles = null,
  } = args
  const model = buildOnePagerModelV2({
    tract,
    address,
    addressApproximate,
    stateLabel,
    dataVintage,
    year,
    nearby,
    siteScore,
    classification,
    blockInputs,
    blockGroupInputs,
    statePercentiles,
  })
  return { model, filename: exportFilename(tract, stateLabel) }
}
